import { z } from "zod";

const bool = z
  .enum(["true", "false"])
  .default("false")
  .transform((v) => v === "true");

const list = z
  .string()
  .default("")
  .transform((v) =>
    v
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
  );

const base = z.object({
  DATABASE_URL: z.string().url(),
  SERTICA_BASE: z.string().url(),
  /** "staging" shows the ribbon and sends no heartbeats (SPEC §11.4). */
  APP_ENV: z.enum(["staging", "production", "development"]).default("development"),
  LOG_LEVEL: z.enum(["debug", "info", "warn", "error"]).default("info"),
  /**
   * "true": the worker also syncs on its own timers (5-min delta, budgets, nightly, snapshot).
   * Off by default: Sertica is read only on demand (a search, a drawer refresh, an opened RFQ
   * page, the Sync button), and the web drops its stale-data alarms.
   */
  SCHEDULED_SYNC: bool,
});

const web = base.extend({
  PORT: z.coerce.number().int().positive().default(3000),
  PUBLIC_ORIGIN: z.string().url(),
  COOKIE_SECURE: bool,
  ADMIN_LOGINS: list,
  /** Sertica role numbers whose sessions are admins (SPEC §13): "O-ADM01" is OFC Administrator. */
  ADMIN_ROLES: list.default("O-ADM01"),
  /** Number of proxy hops in front of web; 0 means use the socket address (ARCHITECTURE §9). */
  TRUST_PROXY_HOPS: z.coerce.number().int().min(0).default(0),
  WEB_DIST: z.string().default("apps/web/dist"),
});

const worker = base.extend({
  /**
   * Optional date (e.g. 2026-01-01): the first copy and the nightly comparison read only records
   * changed since then. Older ones come in when a user asks (a search, a drawer refresh).
   */
  BACKFILL_FROM: z.coerce.date().optional(),
  SERTICA_LOGIN: z.string().min(1),
  SERTICA_PASSWORD: z.string().min(1),
  /**
   * Optional login used only to download attachment files, when the service account has no access
   * to the documents' units in Sertica (research/live-access.md §15). Both or neither.
   */
  SERTICA_FILES_LOGIN: z.string().optional(),
  SERTICA_FILES_PASSWORD: z.string().optional(),
  /**
   * Optional read-only login to Sertica's SQL Server, for budget forecasts, which the API cannot
   * read (research/forecasts.md). Without host, name, user and password, forecasts are not read.
   * Without a port, the port of SERTICA_DB_INSTANCE is asked of the SQL Browser.
   */
  SERTICA_DB_HOST: z.string().optional(),
  SERTICA_DB_PORT: z.coerce.number().int().positive().optional(),
  SERTICA_DB_INSTANCE: z.string().optional(),
  SERTICA_DB_NAME: z.string().optional(),
  SERTICA_DB_USER: z.string().optional(),
  SERTICA_DB_PASSWORD: z.string().optional(),
  /**
   * Optional read-only logins to SEA's and PEA's SQL Servers (AutoCount, research/sea.md), for the
   * vessels with no Sertica unit. Without host, user and password, that source is not read.
   */
  SEA_DB_HOST: z.string().optional(),
  SEA_DB_PORT: z.coerce.number().int().positive().optional(),
  SEA_DB_INSTANCE: z.string().optional(),
  SEA_DB_USER: z.string().optional(),
  SEA_DB_PASSWORD: z.string().optional(),
  PEA_DB_HOST: z.string().optional(),
  PEA_DB_PORT: z.coerce.number().int().positive().optional(),
  PEA_DB_INSTANCE: z.string().optional(),
  PEA_DB_USER: z.string().optional(),
  PEA_DB_PASSWORD: z.string().optional(),
  /** Concurrent requests to Sertica (ARCHITECTURE §8). */
  SERTICA_CONCURRENCY: z.coerce.number().int().min(1).max(4).default(3),
  HEARTBEAT_DELTA_URL: z.string().url().optional(),
  HEARTBEAT_NIGHTLY_URL: z.string().url().optional(),
  HEARTBEAT_SERTICA_URL: z.string().url().optional(),
  HEARTBEAT_BUDGETS_URL: z.string().url().optional(),
});

const migrate = z.object({ DATABASE_URL: z.string().url() });

const schemas = { web, worker, migrate } as const;
export type WebConfig = z.infer<typeof web>;
export type WorkerConfig = z.infer<typeof worker>;
export type MigrateConfig = z.infer<typeof migrate>;
type ConfigOf<P extends keyof typeof schemas> = z.infer<(typeof schemas)[P]>;

/** Parses config for one process; returns either the config or a per-field error list. */
export function parseConfig<P extends keyof typeof schemas>(
  process: P,
  env: Record<string, string | undefined>,
): { ok: true; config: ConfigOf<P> } | { ok: false; errors: string[] } {
  // Treat empty strings as unset so optional keys in an env file can be left blank.
  const cleaned = Object.fromEntries(Object.entries(env).filter(([, v]) => v !== ""));
  const r = schemas[process].safeParse(cleaned);
  if (r.success) return { ok: true, config: r.data as ConfigOf<P> };
  return { ok: false, errors: r.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`) };
}

/** Parses config or exits the process with the error list (ARCHITECTURE §3). */
export function loadConfig<P extends keyof typeof schemas>(p: P): ConfigOf<P> {
  const r = parseConfig(p, process.env);
  if (r.ok) return r.config;
  console.error(JSON.stringify({ level: "error", msg: "invalid config", errors: r.errors }));
  process.exit(1);
}
